import {
    Container,
    BackToHomeScreen,
    PolicyContainer,
    Heading,
    Paragraph,
    NavigationLink,
    NavigationLinkContainer,
    NavigationLinkDivider,
    Li,
} from '../styled'
import NavigationArrowLink from '../../../../reusable/buttons/NavigationArrowLink'
import { useHistory } from 'react-router'
import { useTypedSelector } from '../../../../../hooks'

const TeklaPolicy = () => {
    const history = useHistory()

    const { companyName, organizationalCode } = useTypedSelector(
        (state) => state.whistleblower
    )

    const { lineOfContacts } = useTypedSelector((state) => state.lineOfContact)

    const reportingChannelLink = `https://app.whistleblower.walor.io/organization/${organizationalCode}/whistleblower`

    return (
        <>
            <BackToHomeScreen>
                <NavigationArrowLink
                    onClickCallback={() => {
                        history.push(
                            `/organization/${organizationalCode}/whistleblower`
                        )
                    }}
                    linkText={'Back to Home Screen'}
                />
            </BackToHomeScreen>
            <Container>
                <NavigationLinkContainer>
                    <NavigationLink active={true}>{companyName}</NavigationLink>
                    <NavigationLinkDivider />
                    <NavigationLink active={false}>
                        Grievance Mechanism and Whistleblower Policy
                    </NavigationLink>
                </NavigationLinkContainer>
                <PolicyContainer>
                    <Heading>1. Introduction</Heading>
                    <Paragraph>
                        Tekla is committed to operating with integrity,
                        accountability and respect for the people and
                        environment affected by our business. We believe that
                        open, honest dialogue with our stakeholders is essential
                        and a reflection of who we are as a company.
                    </Paragraph>
                    <Paragraph>
                        This policy establishes a clear, safe and accessible way
                        for anyone affected by Tekla&apos;s operations to raise a
                        concern, report wrongdoing or seek resolution. We take
                        every concern seriously. We will not tolerate
                        retaliation against anyone who raises a concern in good
                        faith.
                    </Paragraph>
                    <Paragraph>
                        This policy is publicly available at teklafabrics.com and
                        applies to all of Tekla&apos;s stakeholder groups:
                        investors, customers, suppliers, employees, community and
                        those raising concerns related to our environmental
                        impact.
                    </Paragraph>
                    <Paragraph>
                        A note on employees: Tekla employees are additionally
                        covered by the Tekla Employee Handbook, which sets out
                        detailed procedures specific to the employment
                        relationship. This policy applies to employees regarding
                        concerns that fall outside the scope of the Employee
                        Handbook and provides the overarching framework that
                        connects all stakeholder-facing procedures.
                    </Paragraph>

                    <Heading>2. Scope</Heading>
                    <Paragraph>This policy covers:</Paragraph>
                    <Paragraph>
                        <ul>
                            <Li>investors and shareholders,</Li>
                            <Li>customers,</Li>
                            <Li>suppliers and supply chain partners,</Li>
                            <Li>
                                Tekla employees (in conjunction with the Employee
                                Handbook),
                            </Li>
                            <Li>community and local stakeholders,</Li>
                            <Li>
                                anyone raising concerns about Tekla&apos;s
                                environmental conduct.
                            </Li>
                        </ul>
                    </Paragraph>
                    <Paragraph>
                        Concerns may relate to Tekla&apos;s social,
                        environmental, governance or business conduct – including
                        but not limited to: supplier relationships, product
                        quality, community impact, negative environmental and
                        social impact, and legal or regulatory compliance.
                    </Paragraph>

                    <Heading>3. Understanding the two mechanisms</Heading>
                    <Paragraph>
                        This policy covers two distinct but related channels. It
                        is important to understand the difference so that
                        concerns reach the right process.
                    </Paragraph>

                    <Heading>3a. Grievance procedure</Heading>
                    <Paragraph>
                        A grievance is a concern raised by a stakeholder about
                        Tekla&apos;s conduct, actions or decisions that have
                        affected them directly or indirectly.
                    </Paragraph>
                    <Paragraph>
                        <ul>
                            <Li>
                                A supplier raising concerns about payment terms
                                or contractual fairness.
                            </Li>
                            <Li>
                                A customer raising concerns about product
                                quality, sustainability claims or service.
                            </Li>
                            <Li>
                                Community raising concerns about Tekla&apos;s
                                local impact.
                            </Li>
                            <Li>
                                An investor raising concerns about governance or
                                responsible business conduct.
                            </Li>
                            <Li>
                                Any stakeholder raising concerns about
                                Tekla&apos;s environmental and social practices.
                            </Li>
                        </ul>
                    </Paragraph>

                    <Heading>3b. Whistleblower (protected disclosure) procedure</Heading>
                    <Paragraph>
                        Whistleblowing involves reporting serious concerns that
                        are in the public interest, where wrongdoing may affect
                        others beyond the individual raising the concern.
                    </Paragraph>
                    <Paragraph>
                        <ul>
                            <Li>
                                Suspected fraud, bribery or financial misconduct.
                            </Li>
                            <Li>Breaches of law or regulation.</Li>
                            <Li>Serious health or safety risks.</Li>
                            <Li>
                                Environmental violations or deliberate
                                concealment.
                            </Li>
                            <Li>
                                Significant ethical violations contrary to
                                Tekla&apos;s values and Code of Conduct.
                            </Li>
                        </ul>
                    </Paragraph>
                    <Paragraph>
                        If you are unsure which route applies to your concern,
                        contact us at grievances@teklafabrics.com and we will
                        guide you to the right process.
                    </Paragraph>

                    <Heading>4. How to raise a concern</Heading>

                    <Heading>4a. Grievances</Heading>
                    <Paragraph>
                        All stakeholders can raise a grievance by contacting us
                        at grievances@teklafabrics.com
                    </Paragraph>
                    <Paragraph>
                        You may submit your concern by email. You do not need to
                        have definitive proof of wrongdoing to raise a concern.
                        If something does not feel right, we want to hear from
                        you.
                    </Paragraph>
                    <Paragraph>
                        When raising a grievance, please include (where
                        possible):
                    </Paragraph>
                    <Paragraph>
                        <ul>
                            <Li>a description of the concern,</Li>
                            <Li>when and how it occurred,</Li>
                            <Li>who or what was involved,</Li>
                            <Li>any supporting information or documentation,</Li>
                            <Li>
                                your preferred contact details (optional –
                                anonymous submissions are accepted).
                            </Li>
                        </ul>
                    </Paragraph>

                    <Heading>4b. Whistleblowing</Heading>
                    <Paragraph>
                        For serious concerns, particularly when anonymity is
                        important, Tekla uses Walor; a secure, confidential and
                        anonymous reporting platform.
                    </Paragraph>
                    <Paragraph>
                        Whistleblowing reports can be submitted via this{' '}
                        <a href={reportingChannelLink}>link</a>
                    </Paragraph>
                    <Paragraph>
                        Walor lets you submit a report anonymously, receive
                        follow-up questions from our team without revealing your
                        identity and track the status of your report via a secure
                        case reference number. You do not need to identify
                        yourself. Anonymous reports are treated with the same
                        seriousness as identified reports.
                    </Paragraph>
                    <Paragraph>
                        Whistleblowing concerns can also be reported at
                        grievances@teklafabrics.com, if preferred.
                    </Paragraph>

                    <Heading>5. Grounds for accepting a grievance</Heading>
                    <Paragraph>
                        We will accept a concern as a formal grievance as it
                        relates to Tekla&apos;s conduct, operations, products,
                        supply chain, or impact on stakeholders or the
                        environment when it is raised in good faith, meaning the
                        person raising it genuinely believes the concern to be
                        valid, even if it later cannot be substantiated.
                    </Paragraph>
                    <Paragraph>
                        We reserve the right to decline a concern as a formal
                        grievance in the following circumstances.
                    </Paragraph>
                    <Paragraph>
                        <ul>
                            <Li>
                                It falls entirely outside of Tekla&apos;s
                                operations or sphere of influence.
                            </Li>
                            <Li>It is clearly malicious or vexatious in intent.</Li>
                            <Li>
                                It has already been fully resolved through a prior
                                process.
                            </Li>
                        </ul>
                    </Paragraph>
                    <Paragraph>
                        Where we do not accept a concern as a formal grievance,
                        we will communicate our reasoning to the complainant
                        within 15 business days.
                    </Paragraph>

                    <Heading>6. The grievance process</Heading>

                    <Heading>Step 1: Acknowledgement (within 5 business days)</Heading>
                    <Paragraph>
                        Upon receipt of a concern, we will send an
                        acknowledgement confirming that we have received it,
                        provide a case reference number, and confirm the name and
                        contact details of the person managing the case.
                    </Paragraph>

                    <Heading>
                        Step 2: Initial assessment (within 10 business days of
                        acknowledgement)
                    </Heading>
                    <Paragraph>We will review the concern and determine:</Paragraph>
                    <Paragraph>
                        <ul>
                            <Li>
                                whether it meets the criteria for a formal
                                grievance,
                            </Li>
                            <Li>
                                the appropriate course of action and level of
                                urgency,
                            </Li>
                            <Li>
                                whether any immediate interim measures are
                                required.
                            </Li>
                        </ul>
                    </Paragraph>
                    <Paragraph>
                        The complainant will be informed of the outcome of this
                        assessment.
                    </Paragraph>

                    <Heading>Step 3: Investigation</Heading>
                    <Paragraph>
                        We will gather relevant facts, which may include
                        reviewing documentation, speaking with relevant parties,
                        and engaging suppliers or third parties where
                        appropriate. The duration of this stage will depend on
                        the complexity of the concern. We aim to complete
                        investigations within 60 calendar days of
                        acknowledgement.
                    </Paragraph>
                    <Paragraph>
                        For complex cases requiring more time, we will notify the
                        complainant and provide an updated timeline.
                    </Paragraph>

                    <Heading>Step 4: Resolution</Heading>
                    <Paragraph>
                        Depending on the nature of the concern, resolution may
                        involve:
                    </Paragraph>
                    <Paragraph>
                        <ul>
                            <Li>
                                corrective action (e.g. process change, policy
                                update, remediation),
                            </Li>
                            <Li>mediation between parties,</Li>
                            <Li>
                                financial remedy or compensation where
                                appropriate,
                            </Li>
                            <Li>
                                escalation to the Managing Director for serious
                                matters,
                            </Li>
                            <Li>
                                referral to the relevant authorities in the case
                                of legal violations.
                            </Li>
                        </ul>
                    </Paragraph>

                    <Heading>Step 5: Communication and closure</Heading>
                    <Paragraph>
                        We will communicate the outcome of the grievance to the
                        complainant, including the steps taken and the resolution
                        reached. Where full disclosure is not possible due to
                        confidentiality obligations, we will explain this. We
                        will confirm when the case has been formally closed.
                    </Paragraph>
                    <Paragraph>
                        If the complainant is not satisfied with the outcome,
                        they may request escalation to the Managing Director
                        within 10 business days of the resolution communication.
                    </Paragraph>

                    <Heading>7. Whistleblower process</Heading>
                    <Paragraph>
                        Whistleblowing reports received via Walor or email are
                        reviewed by the Head of Sustainability upon receipt.
                        Where the report involves the Head of Sustainability, it
                        is directed immediately to the Managing Director.
                    </Paragraph>
                    {lineOfContacts.length > 0 && (
                        <>
                            <Paragraph>
                                Reports submitted through Walor are processed by
                                the following case handlers:
                            </Paragraph>
                            <Paragraph>
                                <ul>
                                    {lineOfContacts.map((lineOfContact) => (
                                        <Li
                                            key={lineOfContact.id}
                                            style={{ paddingBottom: '1rem' }}
                                        >
                                            {lineOfContact.first_name}{' '}
                                            {lineOfContact.last_name}
                                            {' - '}
                                            {
                                                lineOfContact
                                                    .contact_information.position
                                            }
                                        </Li>
                                    ))}
                                </ul>
                            </Paragraph>
                        </>
                    )}
                    <Paragraph>
                        The investigation process follows the same steps outlined
                        in Section 6 above, with additional protections.
                    </Paragraph>
                    <Paragraph>
                        <ul>
                            <Li>
                                The identity of the whistleblower will be
                                protected at all stages. Even when identity is
                                known to the case manager, it will not be shared
                                without the whistleblower&apos;s explicit consent.
                            </Li>
                            <Li>
                                Anonymous reports are accepted and will be
                                investigated based on the information provided.
                            </Li>
                            <Li>
                                The whistleblower will be updated through the
                                Walor platform (or by their preferred contact
                                method if identified) at each key stage of the
                                process.
                            </Li>
                            <Li>
                                All whistleblowing cases are escalated to and
                                reviewed by the Managing Director.
                            </Li>
                        </ul>
                    </Paragraph>

                    <Heading>8. Protection from retaliation</Heading>
                    <Paragraph>
                        Tekla strictly prohibits any form of retaliation against
                        anyone who raises a concern under this policy in good
                        faith. This applies to all stakeholders: employees,
                        suppliers, customers, community members and any other
                        party.
                    </Paragraph>
                    <Paragraph>
                        Retaliation includes, but is not limited to, termination
                        of a business or employment relationship, demotion or
                        exclusion, unfavourable contract changes, harassment,
                        intimidation or any other adverse action linked to the
                        raising of a concern.
                    </Paragraph>
                    <Paragraph>
                        Anyone who experiences retaliation after raising a
                        concern should report this immediately to
                        grievances@teklafabrics.com or via Walor. Incidents of
                        retaliation will be treated with the utmost seriousness
                        and investigated independently.
                    </Paragraph>
                    <Paragraph>
                        Employees who engage in retaliation against a person who
                        has raised a concern may face disciplinary action, up to
                        and including termination of employment.
                    </Paragraph>
                    <Paragraph>
                        Making a deliberately false or malicious report is a
                        violation of this policy and may result in appropriate
                        action. However, raising a concern in good faith that is
                        later unsubstantiated will never result in any adverse
                        consequences.
                    </Paragraph>

                    <Heading>9. Confidentiality</Heading>
                    <Paragraph>
                        All concerns raised under this policy are handled with
                        strict confidentiality. We will share information about a
                        concern only with those who need to know in order to
                        investigate and resolve it.
                    </Paragraph>
                    <Paragraph>
                        Anonymous submissions are accepted through both our
                        grievance email and our Walor whistleblowing platform.
                        Tekla will not attempt to identify anonymous reporters
                        and any information that could reveal identity will be
                        handled with care.
                    </Paragraph>
                    <Paragraph>
                        Where third parties (e.g., suppliers) are involved in an
                        investigation, we will carefully manage the disclosure of
                        information to protect the complainant while enabling a
                        fair process.
                    </Paragraph>

                    <Heading>10. Roles and responsibilities</Heading>

                    <Heading>Head of Sustainability</Heading>
                    <Paragraph>
                        <ul>
                            <Li>
                                Primary point of contact and case manager for all
                                grievances and whistleblower reports.
                            </Li>
                            <Li>Conducts or oversees investigations.</Li>
                            <Li>
                                Maintains the grievance log and prepares the
                                annual summary.
                            </Li>
                            <Li>
                                Escalates cases to the Managing Director as
                                appropriate.
                            </Li>
                            <Li>
                                Reviews the policy annually and recommends
                                updates.
                            </Li>
                        </ul>
                    </Paragraph>

                    <Heading>Managing Director</Heading>
                    <Paragraph>
                        <ul>
                            <Li>Receives escalated or serious cases.</Li>
                            <Li>Reviews the annual grievance summary report.</Li>
                            <Li>Responsible for approving this policy.</Li>
                            <Li>
                                Oversight of grievance resolution for investor and
                                customer concerns.
                            </Li>
                        </ul>
                    </Paragraph>

                    <Heading>Head of People &amp; Culture (HR)</Heading>
                    <Paragraph>
                        <ul>
                            <Li>
                                Manages employee-specific grievances in line with
                                the Employee Handbook.
                            </Li>
                            <Li>
                                Cooperates with the Managing Director and/or Head
                                of Sustainability on any cross-cutting matters.
                            </Li>
                        </ul>
                    </Paragraph>

                    <Heading>11. Tracking, monitoring &amp; annual reporting</Heading>
                    <Paragraph>
                        Tekla maintains a confidential grievance log, managed by
                        the Head of Sustainability. For each case, the log
                        records:
                    </Paragraph>
                    <Paragraph>
                        <ul>
                            <Li>Date received</Li>
                            <Li>
                                Stakeholder group (e.g. supplier, customer,
                                community),
                            </Li>
                            <Li>Nature of the concern (categorised by topic)</Li>
                            <Li>
                                Current status (open / under investigation /
                                resolved / escalated / declined)
                            </Li>
                            <Li>Date of resolution or closure</Li>
                            <Li>Outcome summary (anonymised)</Li>
                        </ul>
                    </Paragraph>
                    <Paragraph>
                        Tekla prepares an Annual Grievance Summary each year,
                        which includes:
                    </Paragraph>
                    <Paragraph>
                        <ul>
                            <Li>
                                Total number of concerns received, by stakeholder
                                group and category
                            </Li>
                            <Li>
                                Number resolved, pending, escalated, or declined
                            </Li>
                            <Li>Key themes or patterns observed</Li>
                            <Li>
                                Assessment of the effectiveness of the grievance
                                procedure
                            </Li>
                            <Li>
                                Recommended improvements for the following year
                            </Li>
                        </ul>
                    </Paragraph>
                    <Paragraph>
                        This summary is reviewed by the Managing Director and used
                        to inform continuous improvement of Tekla&apos;s grievance
                        procedures.
                    </Paragraph>

                    <Heading>12. External reporting</Heading>
                    <Paragraph>
                        While we encourage concerns to be raised directly with
                        Tekla in the first instance, we recognise that there may
                        be circumstances in which a stakeholder chooses to report
                        concerns to an external authority or regulator. We fully
                        support this right.
                    </Paragraph>
                    <Paragraph>Examples of the relevant Danish external bodies.</Paragraph>
                    <Paragraph>
                        <ul>
                            <Li>
                                Datatilsynet (Danish Data Protection Agency): for
                                concerns about data protection.
                            </Li>
                            <Li>
                                Erhvervsstyrelsen (Danish Business Authority): for
                                concerns about company law and governance.
                            </Li>
                            <Li>
                                Arbejdstilsynet (Danish Working Environment
                                Authority): for concerns about health and safety.
                            </Li>
                            <Li>
                                Relevant environmental authorities for concerns
                                about environmental harm.
                            </Li>
                        </ul>
                    </Paragraph>
                    <Paragraph>
                        Employees who wish to report to external authorities are
                        protected from retaliation for doing so, in line with
                        Danish legislation implementing the EU Whistleblower
                        Directive (Directive 2019/1937).
                    </Paragraph>

                    <Heading>13. Policy review</Heading>
                    <Paragraph>
                        This policy will be reviewed annually by the Head of
                        Sustainability and approved by the Managing Director. It
                        will also be reviewed following any significant change in
                        legislation, B Corp standards, or Tekla&apos;s business
                        operations.
                    </Paragraph>
                </PolicyContainer>
            </Container>
        </>
    )
}

export default TeklaPolicy
